import { isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { t } from 'i18next';

import { toastActions } from '@/entities/toast';
import { BadRequestError } from '@/shared/constants/bad-request.constant';
import { ConflictError } from '@/shared/constants/conflict-error.constant';
import {
	NoDataError,
	NoDataErrorTranslation,
	NoDataErrorWithoutDetail,
} from '@/shared/constants/no-data-error.constant';
import { StatusCodeError } from '@/shared/constants/status-code-error.constant';
import { UnprocessableEntityError } from '@/shared/constants/unprocessable-entity-error.constant';

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
	const params = new URLSearchParams(window.location.search);
	const isFirstAccess = JSON.parse(params.get('isFirstAccess') || 'false');

	if (isRejectedWithValue(action)) {
		if (action.payload.status === StatusCodeError.MultipleChoices) {
			return next(action);
		}

		if (
			action.payload.status === StatusCodeError.Conflict &&
			action.payload.data?.detail?.startsWith(ConflictError.GroupAlreadyExists)
		) {
			return next(action);
		}

		if (action.payload.status === StatusCodeError.UnprocessableEntity) {
			const message = action.payload.data?.detail?.[0]?.msg;

			if (Object.values(UnprocessableEntityError).includes(message)) {
				api.dispatch(
					toastActions.addToast({
						type: 'error',
						title: t('toast.error.title'),
						message,
					}),
				);
			}

			return next(action);
		}

		if (
			action.payload.status === StatusCodeError.FetchError ||
			action.payload.originalStatus === StatusCodeError.RequestEntityTooLarge
		) {
			return next(action);
		}

		if (
			action.payload.status === StatusCodeError.Unauthorized ||
			action.payload.originalStatus === StatusCodeError.Unauthorized
		) {
			api.dispatch(
				toastActions.addToast({
					message: isFirstAccess ? t('toast.error.first.access.expiredToken') : t('toast.error.expiredToken'),
					type: 'error',
					title: t('toast.error.title'),
				}),
			);

			return next(action);
		}

		if (action.payload.status === StatusCodeError.BadRequest) {
			const backendData = action.payload.data;
			const backendMessage = backendData?.detail ?? JSON.parse(backendData)?.detail;

			if ([BadRequestError.InvalidCurrentPassword].includes(backendMessage)) {
				return next(action);
			}

			if (Object.values(BadRequestError).includes(backendMessage)) {
				api.dispatch(
					toastActions.addToast({
						message: backendMessage,
						type: 'error',
						title: t('toast.error.title'),
					}),
				);

				return next(action);
			}
		}

		if (action.payload.status === StatusCodeError.NoData) {
			const backendMessage = (action.payload.data?.detail ?? JSON.parse(action.payload.data)?.detail) as NoDataError;
			const backendData = action.payload.data as NoDataErrorWithoutDetail;

			let message = '';

			if (
				typeof backendData === 'string' &&
				Object.values(NoDataErrorWithoutDetail).some((data) => backendData.startsWith(data))
			) {
				message = action.payload.data;
			} else if (Object.values(NoDataError).includes(backendMessage)) {
				message = t(NoDataErrorTranslation[backendMessage]);
			} else {
				message = t('toast.error.no.data.on.boat.error');
			}

			api.dispatch(
				toastActions.addToast({
					message,
					type: 'error',
					title: t('toast.error.title'),
				}),
			);
		} else {
			api.dispatch(
				toastActions.addToast({
					message:
						action.payload.data?.detail?.[0]?.msg ??
						action.payload.data?.detail?.message ??
						action.payload.data?.detail ??
						action.payload.data?.message ??
						t('toast.error.serverError'),
					type: 'error',
					title: t('toast.error.title'),
				}),
			);
		}
	}

	return next(action);
};
